import React, { createContext, useContext, useState } from 'react';
import { toast } from 'react-toastify';
import { markAttendance as apiMarkAttendance } from '../services/mentorServices/MentorServices';

const AttendanceContext = createContext();

const getStorageKey = () => {
    const today = new Date().toISOString().split('T')[0];
    return `marked_attendance_${today}`;
};

export const AttendanceProvider = ({ children }) => {
    const [markedLearners, setMarkedLearners] = useState(() => {
        try {
            const key = getStorageKey();
            const saved = sessionStorage.getItem(key);
            return saved ? JSON.parse(saved) : {};
        } catch (e) {
            return {};
        }
    });

    const [markingAttendanceId, setMarkingAttendanceId] = useState(null);

    const recordAttendance = (learnerId) => {
        if (!learnerId) return;
        setMarkedLearners(prev => {
            const updated = { ...prev, [String(learnerId)]: true };
            try {
                sessionStorage.setItem(getStorageKey(), JSON.stringify(updated));
            } catch (e) {
                // ignore storage quota issues
            }
            return updated;
        });
    };

    const isAttendanceMarked = (learnerId) => {
        if (!learnerId) return false;
        return !!markedLearners[String(learnerId)];
    };

    const markAttendanceAction = async (learnerId) => {
        if (!learnerId) {
            toast.error("Learner ID not found.");
            return false;
        }

        setMarkingAttendanceId(String(learnerId));
        try {
            const response = await apiMarkAttendance(learnerId);
            recordAttendance(learnerId);
            toast.success(response?.data?.message || "Learner attendance marked successfully by mentor");
            return true;
        } catch (error) {
            const msg = error?.response?.data?.message || error?.response?.data?.error || error?.message || "";
            if (msg.toLowerCase().includes("already marked")) {
                recordAttendance(learnerId);
                toast.info(msg);
                return true;
            }
            toast.error(msg || "Failed to mark attendance");
            return false;
        } finally {
            setMarkingAttendanceId(null);
        }
    };

    return (
        <AttendanceContext.Provider
            value={{
                markedLearners,
                isAttendanceMarked,
                recordAttendance,
                markAttendanceAction,
                markingAttendanceId,
            }}
        >
            {children}
        </AttendanceContext.Provider>
    );
};

export const useAttendance = () => {
    const context = useContext(AttendanceContext);
    if (!context) {
        throw new Error('useAttendance must be used within an AttendanceProvider');
    }
    return context;
};

export default AttendanceContext;
